/**
 * ConceptSelect — Selector de elementos del Catálogo Maestro (V1.44)
 * ===================================================================
 * Los movimientos apuntan SIEMPRE a un elemento final del catálogo
 * (catalog_items), nunca a una agrupación. Muestra la ruta jerárquica
 * de la agrupación del elemento (Vivienda › Servicios › Luz Vivienda).
 * Permite crear elementos inline con su agrupación.
 */
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Plus, Search, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import GroupingSelect from "./GroupingSelect";

const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();

const _cache = {};

async function fetchConcepts(kind, force = false) {
  if (!force && _cache[kind]) return _cache[kind];
  const r = await api.get("/catalog-items", { params: { kind } });
  _cache[kind] = r.data || [];
  return _cache[kind];
}

export function invalidateConceptCache(kind) {
  if (kind) delete _cache[kind];
  else Object.keys(_cache).forEach((k) => delete _cache[k]);
}

export default function ConceptSelect({
  kind,                    // gasto | ingreso | activo | compromiso | obligacion
  value,                   // concept_id o null
  onChange,                // (id, item) => void
  legacyLabel = "",        // texto histórico si el registro no tiene concept_id
  placeholder = "Selecciona un concepto del catálogo…",
  disabled = false,
  testId = "concept-select",
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newGroupId, setNewGroupId] = useState(null);

  useEffect(() => { if (kind) fetchConcepts(kind).then(setItems).catch(() => {}); }, [kind]);

  const selected = useMemo(() => items.find((f) => f.id === value) || null, [items, value]);

  const filtered = useMemo(() => {
    const q = norm(query);
    const arr = items.filter((f) => f.active !== false);
    if (!q) return arr;
    return arr.filter((f) => norm(f.name).includes(q) || norm((f.group_path || []).join(" ")).includes(q));
  }, [items, query]);

  const exactMatch = useMemo(() => {
    if (!query.trim()) return null;
    return items.find((f) => norm(f.name) === norm(query)) || null;
  }, [items, query]);

  const doCreate = async () => {
    const name = (newName || query).trim();
    if (!name) return;
    const dup = items.find((f) => norm(f.name) === norm(name));
    if (dup) {
      onChange(dup.id, dup);
      setOpen(false); setCreating(false); setQuery(""); setNewName("");
      toast.info(`"${dup.name}" ya existe — se ha seleccionado.`);
      return;
    }
    try {
      const r = await api.post("/catalog-items", { kind, name, group_id: newGroupId, active: true });
      invalidateConceptCache(kind);
      const list = await fetchConcepts(kind, true);
      setItems(list);
      onChange(r.data.id, r.data);
      setOpen(false); setCreating(false); setQuery(""); setNewName(""); setNewGroupId(null);
      toast.success(`Concepto "${r.data.name}" creado`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const displayLabel = selected
    ? selected.name
    : (legacyLabel ? `${legacyLabel} · (histórico — vincular)` : "");
  const selectedPath = selected && (selected.group_path || []).length > 0 ? selected.group_path.join(" › ") : "";

  return (
    <div className="relative" data-testid={testId}>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setCreating(false); setQuery(""); } }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={`${testId}-trigger`}
            className={`w-full flex items-center justify-between gap-2 min-h-10 px-3 py-2 rounded-md border border-input bg-background text-sm text-left hover:border-foreground/40 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <Tag size={14} className="text-muted-foreground shrink-0" />
              <span className="min-w-0">
                <span className={`block truncate ${!selected && !legacyLabel ? "text-muted-foreground" : ""} ${legacyLabel && !selected ? "text-amber-700 italic" : ""}`}>
                  {displayLabel || placeholder}
                </span>
                {selectedPath && <span className="block truncate text-[10px] text-muted-foreground">{selectedPath}</span>}
              </span>
            </span>
            <ChevronsUpDown size={14} className="text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-md" align="start">
          {!creating ? (
            <div>
              <div className="relative border-b border-border">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  autoFocus
                  data-testid={`${testId}-search`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar concepto…"
                  className="w-full h-10 pl-9 pr-3 text-sm bg-transparent focus:outline-none"
                />
              </div>
              <div className="max-h-60 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">Sin resultados{query.trim() ? ` para "${query}"` : ""}.</div>
                ) : (
                  filtered.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      data-testid={`${testId}-option-${f.id}`}
                      onClick={() => { onChange(f.id, f); setOpen(false); setQuery(""); }}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-secondary/60 text-left"
                    >
                      <div className="min-w-0">
                        <span className="block truncate">{f.name}</span>
                        {(f.group_path || []).length > 0 && (
                          <span className="block truncate text-[10px] text-muted-foreground">{f.group_path.join(" › ")}</span>
                        )}
                      </div>
                      {value === f.id && <Check size={14} className="text-primary shrink-0" />}
                    </button>
                  ))
                )}
              </div>
              <div className="border-t border-border p-2 bg-secondary/30 flex gap-2">
                {value && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => { onChange(null, null); setOpen(false); }}>Quitar</Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 justify-start"
                  data-testid={`${testId}-create-btn`}
                  onClick={() => { setCreating(true); setNewName(query.trim()); }}
                  disabled={!!exactMatch}
                >
                  <Plus size={14} className="mr-1" />
                  {exactMatch ? `Ya existe: ${exactMatch.name}` : (query.trim() ? `Crear "${query.trim()}"` : "Crear nuevo concepto")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Nuevo concepto del catálogo</div>
              <Input
                autoFocus
                data-testid={`${testId}-new-name`}
                placeholder="Nombre (ej. Luz Vivienda, Nómina Empresa…)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doCreate()}
              />
              <div>
                <label className="text-[11px] text-muted-foreground">Agrupación (opcional)</label>
                <GroupingSelect kind={kind} value={newGroupId} onChange={setNewGroupId} testId={`${testId}-new-group`} />
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => setCreating(false)}>Cancelar</Button>
                <Button type="button" size="sm" className="flex-1" onClick={doCreate} disabled={!newName.trim()} data-testid={`${testId}-create-confirm`}>Crear y seleccionar</Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
